import { useMemo } from 'react'
import { Dropdown } from '~/components/Form'
import type {
  DropdownProps,
  SubOptionConfig
} from '~/components/Form/Input/Dropdown'
import { useQualificationsList } from '../queries/QualificationsList'
import type { QualificationWithParent } from '../types'

export const QualificationsDropdown = ({
  withSpecialities = false,
  ...props
}: DropdownProps & { withSpecialities?: boolean }) => {
  const { data, isLoading } = useQualificationsList()

  const treeData = useMemo(() => {
    const byParent = new Map<number, QualificationWithParent[]>()
    data?.forEach((qualification) => {
      byParent.set(qualification.parent?.id ?? 0, [
        ...(byParent.get(qualification.parent?.id ?? 0) ?? []),
        qualification
      ])
    })
    const buildChildren = (parentId: number): DropdownProps['options'] => {
      const children = byParent.get(parentId) ?? []
      return children.map((child) => ({
        label: child.name,
        value: String(child.id),
        children: buildChildren(child.id)
      }))
    }
    return buildChildren(0)
  }, [data])

  const subOptions = useMemo<SubOptionConfig | undefined>(() => {
    if (!withSpecialities) return undefined
    const result: SubOptionConfig = {}
    data?.forEach((qualification) => {
      if (qualification.specialities && qualification.specialities.length > 0) {
        result[String(qualification.id)] = qualification.specialities.map(
          (s) => ({
            label: s.name,
            value: String(s.id)
          })
        )
      }
    })
    return Object.keys(result).length > 0 ? result : undefined
  }, [data, withSpecialities])

  return (
    <Dropdown
      {...props}
      optionsLoading={isLoading}
      searchable={true}
      options={treeData}
      subOptions={subOptions}
      allowMultipleSubOptions
    />
  )
}
